import { useState, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import Header from "../components/Header";
import { ArrowRight, Mail, Lock } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user && typeof user === "object") {
      nav(user.role === "admin" ? "/admin" : "/dashboard");
    }
  }, [user, nav]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const r = await login(email, password);
    setSubmitting(false);
    if (!r.ok) {
      toast.error(r.error || "Login failed");
    } else {
      toast.success("Welcome back!");
      const next = new URLSearchParams(loc.search).get("next") || "/dashboard";
      nav(next);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#FAFAFA]">
      <Header />
      <div className="flex-1 grid place-items-center px-6 py-16">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-[#0F172A] tracking-tight">Welcome back</h1>
            <p className="text-sm text-[#475569] mt-2">Sign in to manage your domains and aliases.</p>
          </div>
          <form onSubmit={onSubmit} className="bg-white rounded-2xl border border-[#E2E8F0] p-8 shadow-sm space-y-5" data-testid="login-form">
            <label className="block">
              <span className="text-sm font-medium text-[#0F172A]">Email</span>
              <div className="mt-1.5 relative">
                <Mail className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
                <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  data-testid="login-email-input" placeholder="you@example.com"
                  className="w-full pl-10 pr-3 py-2.5 rounded-lg border border-[#E2E8F0] focus:ring-2 focus:ring-[#F26953]/20 focus:border-[#F26953] outline-none text-sm"/>
              </div>
            </label>
            <label className="block">
              <span className="text-sm font-medium text-[#0F172A]">Password</span>
              <div className="mt-1.5 relative">
                <Lock className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
                <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password-input"
                  className="w-full pl-10 pr-3 py-2.5 rounded-lg border border-[#E2E8F0] focus:ring-2 focus:ring-[#F26953]/20 focus:border-[#F26953] outline-none text-sm"/>
              </div>
            </label>
            <button type="submit" disabled={submitting} data-testid="login-submit-btn"
              className="w-full inline-flex items-center justify-center gap-2 bg-[#F26953] text-white font-semibold py-3 rounded-lg hover:bg-[#D85340] disabled:opacity-60 transition-colors">
              {submitting ? "Signing in…" : <>Sign in <ArrowRight className="h-4 w-4"/></>}
            </button>
            <div className="flex items-center justify-between text-sm">
              <Link to="/forgot-password" data-testid="login-forgot-link" className="text-[#475569] hover:text-[#F26953] hover:underline">
                Forgot password?
              </Link>
              <p className="text-[#475569]">
                No account? <Link to="/signup" data-testid="login-to-signup-link" className="text-[#F26953] font-semibold hover:underline">Create one</Link>
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
